<template>
  <form class="inovio-pay-form form-box">
    <div
      v-if="!$auth.check()"
      class="form-group"
      :class="{ error: $v.form.email.$error }"
    >
      <label>
        {{ $t("labels.email") }}
      </label>
      <div class="input-group">
        <input
          type="text"
          required="required"
          :placeholder="$t('placeholder.email')"
          class="form-control"
          v-model="form.email"
          @input="$v.form.email.$touch"
        />
      </div>
      <template v-if="$v.form.email.$error">
        <p class="error-message" v-if="!$v.form.email.required">
          {{ $t("validations.required", { field: $t("fields.email") }) }}
        </p>
      </template>
    </div>
    <div
      v-if="!$auth.check()"
      class="form-group"
      :class="{ error: $v.form.email_confirm.$error }"
    >
      <label>
        {{ $t("labels.email_confirm") }}
      </label>
      <div class="input-group">
        <input
          type="text"
          required="required"
          :placeholder="$t('placeholder.email_confirm')"
          class="form-control"
          v-model="form.email_confirm"
          @input="$v.form.email_confirm.$touch"
        />
      </div>
      <template v-if="$v.form.email_confirm.$error">
        <p class="error-message" v-if="!$v.form.email_confirm.required">
          {{ $t("validations.sameAs", { field: $t("fields.email") }) }}
        </p>
      </template>
    </div>
    <div class="form-group" :class="{ error: $v.form.card_number.$error }">
      <label>
        {{ $t("labels.card_number") }}
      </label>
      <input
        type="number"
        step="1"
        :placeholder="$t('placeholder.card_number')"
        class="form-control"
        :disabled="loading"
        v-model="form.card_number"
        @input="$v.form.card_number.$touch"
      />
      <template v-if="$v.form.card_number.$error">
        <p class="error-message" v-if="!$v.form.card_number.required">
          {{ $t("validations.required", { field: $t("fields.card_number") }) }}
        </p>
        <p class="error-message" v-else-if="!$v.form.card_number.card_number">
          {{
            $t("validations.card_number", { field: $t("fields.card_number") })
          }}
        </p>
      </template>
    </div>

    <div class="card-line">
      <div
        class="form-group card-expiry"
        :class="{ error: $v.form.card_expiry.month.$error }"
      >
        <label>
          {{ $t("labels.card_expiry") }}
        </label>
        <div class="input-group">
          <input
            type="text"
            maxlength="2"
            minlength="2"
            :placeholder="$t('placeholder.card_expiry_month')"
            class="form-control month"
            :disabled="loading"
            v-model="form.card_expiry.month"
            @input="$v.form.card_expiry.month.$touch"
          />
          <input
            type="text"
            maxlength="2"
            minlength="2"
            :placeholder="$t('placeholder.card_expiry_year')"
            class="form-control year"
            :disabled="loading"
            v-model="form.card_expiry.year"
            @input="$v.form.card_expiry.year.$touch"
          />
        </div>
        <template v-if="$v.form.card_expiry.month.$error">
          <p class="error-message" v-if="!$v.form.card_expiry.month.required">
            {{
              $t("validations.required", {
                field: $t("fields.card_expiry_month"),
              })
            }}
          </p>
          <p
            class="error-message"
            v-else-if="!$v.form.card_expiry.month.card_expiry_month"
          >
            {{
              $t("validations.card_expiry_month", {
                field: $t("fields.card_expiry_month"),
              })
            }}
          </p>
        </template>
        <template v-if="$v.form.card_expiry.year.$error">
          <p class="error-message" v-if="!$v.form.card_expiry.year.required">
            {{
              $t("validations.required", {
                field: $t("fields.card_expiry_year"),
              })
            }}
          </p>
          <p
            class="error-message"
            v-else-if="!$v.form.card_expiry.month.card_expiry_year"
          >
            {{
              $t("validations.card_expiry_year", {
                field: $t("fields.card_expiry_year"),
              })
            }}
          </p>
        </template>
      </div>

      <div
        class="form-group card_key"
        :class="{ error: $v.form.card_key.$error }"
      >
        <label>
          {{ $t("labels.card_key") }}
        </label>
        <div class="input-group">
          <input
            type="text"
            maxlength="4"
            minlength="4"
            :placeholder="$t('placeholder.card_key')"
            class="form-control"
            :disabled="loading"
            v-model="form.card_key"
            @input="$v.form.card_key.$touch"
          />
        </div>
        <template v-if="$v.form.card_key.$error">
          <p class="error-message" v-if="!$v.form.card_key.required">
            {{ $t("validations.required", { field: $t("fields.card_key") }) }}
          </p>
          <p class="error-message" v-else-if="!$v.form.card_key.card_key">
            {{ $t("validations.card_key", { field: $t("fields.card_key") }) }}
          </p>
        </template>
      </div>
    </div>

    <!-- Processor -->
    <component
      :is="processorFormComponent"
      :config="paymentMethod.driver_front_config.processor_config"
      :processorDataChangedHandler="processorDataChangedHandler"
      :loading="loading"
      :submitting="submitting"
    />
    <!-- Processor -->

    <div class="legals">
      <div class="form-group" :class="{ error: $v.form.over_age.$error }">
        <label>
          <input type="checkbox" v-model="form.over_age" />
          <i18n
            path="subscription.payment_legals.accept.over_age"
            tag="span"
          ></i18n>
        </label>
        <template v-if="$v.form.over_age.$error">
          <p class="error-message" v-if="!$v.form.over_age.accepted">
            {{ $t("validations.accepted", { field: $t("fields.terms") }) }}
          </p>
        </template>
      </div>
      <div class="form-group" :class="{ error: $v.form.terms.$error }">
        <label>
          <input type="checkbox" v-model="form.terms" />
          <i18n
            :path="`subscription.payment_legals.accept.${paymentLegalsAceeptLabel}`"
            tag="span"
          >
            <template>
              <a :href="termsUrl" target="_blank">{{
                $t("subscription.payment_legals.accept.terms")
              }}</a>
            </template>
            <template>
              <a :href="privacyUrl">{{
                $t("subscription.payment_legals.accept.privacy")
              }}</a>
            </template>
            <template>
              <router-link :to="{ name: 'support' }">{{
                $t("zammad_support.link")
              }}</router-link>
            </template>
          </i18n>
        </label>
        <template v-if="$v.form.terms.$error">
          <p class="error-message" v-if="!$v.form.terms.accepted">
            {{ $t("validations.accepted", { field: $t("fields.terms") }) }}
          </p>
        </template>
      </div>
    </div>

    <ResultMessage
      v-if="resultMessage.message"
      :result-message="resultMessage"
    />

    <div class="buttons-container">
      <template v-if="form.accept_no_trial">
        <p class="no-trial-message">
          {{ $t("subscription.not_elegible_for_free_trial") }}
        </p>
        <button
          type="button"
          class="btn main-btn submit"
          @click="startSubmitHandler"
        >
          {{ $t("subscription.buttons.pay_without_trial") }}
        </button>
      </template>
      <template v-else-if="form.accept_no_promo">
        <p class="no-promo-message">
          {{ $t("subscription.promo_unsuported") }}
        </p>
        <button
          type="button"
          class="btn main-btn submit"
          @click="startSubmitHandler"
        >
          {{ $t("subscription.buttons.pay_without_promo") }}
        </button>
      </template>
      <template v-else>
        <button
          type="button"
          class="btn main-btn submit"
          @click="startSubmitHandler"
        >
          {{ $t("subscription.buttons.pay") }}
        </button>
      </template>
    </div>

    <i-frame
      v-if="form.iframeLoaded"
      :ddc_url="form.iframeData.ddc_url"
      :jwt="form.iframeData.jwt"
      :bin="form.iframeData.bin"
      @message="messageHandler"
    />
  </form>
</template>
<script>
import Spinner from "../Spinner";
import { required, requiredIf, email } from "vuelidate/lib/validators";
import CieloProcessor from "./SubscriptionForm.Purchase.InovioPayForm.Processor.Cielo";
import WorldlineProcessor from "./SubscriptionForm.Purchase.InovioPayForm.Processor.Worldline";
import RyvylProcessor from "./Purchase/InovioPayForm/Processor/Ryvyl";
import iFrame from "./SubscriptionForm.Purchase.InovioPayIframe";
import luhn from "luhn";
import Vue from "vue";
import ResultMessage from "../ResultMessage";
import EventBus from "../../eventbus";
import resources from "../../resources";

export default {
  components: {
    Spinner,
    CieloProcessor,
    WorldlineProcessor,
    RyvylProcessor,
    iFrame,
    ResultMessage,
  },
  props: {
    purchaseOption: {
      type: Object,
      required: true,
    },
    hasToAcceptNoTrial: {
      required: true,
      default: false,
    },
    hasToAcceptNoPromo: {
      required: true,
      default: false,
    },
    resultMessage: {
      required: true,
    },
    paymentMethod: {
      required: true,
      type: Object,
    },
    selectedCountry: {
      required: false,
      type: Object,
    },
    recurrent: {
      required: true,
      type: Boolean,
      default: true,
    },
    paymentCountry: {
      required: true,
    },
  },
  data() {
    return {
      form: {
        email: "",
        email_confirm: "",
        card_number: "",
        card_expiry: {
          month: "",
          year: "",
        },
        card_key: "",
        accept_no_trial: false,
        accept_no_promo: false,
        terms: false,
        over_age: false,
        processor: {},
        iframeLoaded: false,
        iframeData: {
          ddc_url: "",
          jwt: "",
          bin: "",
          ddc_referenceid: "",
        },
        addon_legacy: false,
        payment_country_id: null,
      },
      loading: false,
      processorDataValid: false,
      submitting: false,
    };
  },
  mounted() {
    this.form.payment_country_id = this.paymentCountry;

    window.addEventListener("message", this.messageHandler, false);

    EventBus.$on("addon_legacy", (value) => (this.form.addon_legacy = value));
  },
  methods: {
    submitHandler() {
      this.submitting = true;

      this.$nextTick().then(() => {
        this.submitting = false;
      });

      if (this.$v.form.$invalid) {
        this.$v.form.$touch();
      } else {
        this.$emit("submit", this.form);
      }
    },
    processorDataChangedHandler(processorData, valid) {
      this.processorDataValid = !!valid;
      this.$set(this.form, "processor", processorData);
    },
    startSubmitHandler() {
      if (this.$v.form.$invalid) {
        this.$v.form.$touch();
      } else {
        this.form.iframeLoaded = false;
        this.$emit("loadingChange", true);

        resources.paymentsInovioRetrieveDdc
          .store({
            country_id: this.selectedCountry ? this.selectedCountry.id : "INT",
            form_data: this.form,
            payment_method_id: this.paymentMethod.id,
          })
          .then((response) => {
            // TODO: validar respuesta correcta
            this.form.iframeData.ddc_url = response.body.DDC_URL;
            this.form.iframeData.jwt = response.body.JWT;
            this.form.iframeData.bin = this.form.card_number.substring(0, 6);
            this.form.iframeData.ddc_referenceid =
              response.body.DDC_REFERENCEID;
            this.form.iframeLoaded = true;
          })
          .catch((err) => {
            // console.log({err});
            this.$emit("loadingChange", false);
          });
        // let writeBaseUrl = process.env.VUE_APP_ROOT_API_WRITE.replace(/\/+$/, "");
        // // get from backend the jwt param and populate iframe
        // Vue.http.post(`${writeBaseUrl}/paymentsInovioRetrieveDdc`, {
        //     country_id: this.selectedCountry ? this.selectedCountry.id : 'INT',
        //     form_data: this.form,
        //     payment_method_id: this.paymentMethod.id,
        // }).then((resp) => {
        //     // TODO: validar respuesta correcta
        //     this.form.iframeData.ddc_url            = resp.body.DDC_URL;
        //     this.form.iframeData.jwt                = resp.body.JWT;
        //     this.form.iframeData.bin                = this.form.card_number.substring(0, 6);
        //     this.form.iframeData.ddc_referenceid    = resp.body.DDC_REFERENCEID;
        //     this.form.iframeLoaded = true;
        // }).catch(err => {
        //     // console.log({err});
        //     this.$emit('loadingChange', false);
        // })
      }
    },
    messageHandler(event) {
      if (event.origin === this.ddcMessageSender) {
        if (!this.submitting) {
          console.log("confirmado");
          this.$emit("loadingChange", false);
          this.submitHandler();
        }
      }
    },
  },
  watch: {
    "form.card_number"() {
      this.form.accept_no_trial = false;
    },
    "form.card_expiry": {
      handler() {
        this.form.accept_no_trial = false;
      },
      deep: true,
    },
    "form.card_key"() {
      this.form.accept_no_trial = false;
    },
    hasToAcceptNoTrial(hasToAcceptNoTrial) {
      if (hasToAcceptNoTrial === null) {
        this.form.accept_no_trial = false;
      } else {
        this.form.accept_no_trial = !!hasToAcceptNoTrial;
      }
    },
    hasToAcceptNoPromo(hasToAcceptNoPromo) {
      if (hasToAcceptNoPromo === null) {
        this.form.accept_no_promo = false;
      } else {
        this.form.accept_no_promo = !!hasToAcceptNoPromo;
      }
    },
    paymentCountry(newVal) {
      this.form.payment_country_id = newVal;
    },
  },
  computed: {
    paymentLegalsAceeptLabel() {
      if (this.recurrent) return "label_recurrent";
      return "one_payment";
    },
    termsUrl() {
      let langCode = this.$i18n.locale;
      return `/legals/terms/${langCode}.html`;
    },
    privacyUrl() {
      return "https://divasplay.com/legals/privacy.html";
    },
    supportAddress() {
      return `soporte@divasplay.com`;
    },
    processorFormComponent() {
      switch (this.paymentMethod.driver_front_config.processor) {
        case "cielo":
          return "CieloProcessor";
        case "inovio":
        case "worldline":
          return "WorldlineProcessor";
        case "ryvyl":
          return "RyvylProcessor";
        default:
          throw Error(
            `Unknown processor key "${this.paymentMethod.driver_front_config.processor}"`
          );
      }
    },
    ddcMessageSender() {
      let ddcUrlSplit = this.form.iframeData.ddc_url.split("/");
      return (ddcUrlSplit[0] || "no") + "//" + (ddcUrlSplit[2] || "url");
    },
  },
  validations() {
    return {
      form: {
        email: {
          requiredIf: requiredIf(function () {
            //Si no esta logueado tiene que completar el email
            return !this.$auth.check();
          }),
          email,
        },
        email_confirm: {
          requiredIf: requiredIf(function () {
            //Si no esta logueado tiene que completar el email
            return !this.$auth.check();
          }),
          sameAs(value) {
            return value === this.form.email;
          },
        },
        card_number: {
          required,
          card_number(value) {
            const intString = parseInt(value).toString();
            return (
              intString === value &&
              [14, 15, 16].includes(intString.length) &&
              luhn.validate(value)
            );
          },
        },
        card_expiry: {
          month: {
            required,
            card_expiry_month(value) {
              return (
                value.toString().length === 2 &&
                value >= 1 &&
                value <= 12 &&
                (this.form.card_expiry.year >
                  parseInt(new Date().getFullYear().toString().substr(-2)) ||
                  value >= new Date().getMonth() + 1)
              );
            },
          },
          year: {
            required,
            card_expiry_year(value) {
              return (
                value.toString().length === 2 &&
                value >= 1 &&
                value <= 99 &&
                value >=
                  parseInt(new Date().getFullYear().toString().substr(-2))
              );
            },
          },
        },
        card_key: {
          required,
          card_key(value) {
            return /^\d*$/.test(value) && [3, 4].includes(value.length);
          },
        },
        terms: {
          accepted(value) {
            return value === true;
          },
        },
        processor: {
          valid(value) {
            return !!this.processorDataValid;
          },
        },
        over_age: {
          accepted(value) {
            return value === true;
          },
        },
      },
    };
  },
};
</script>
<style scoped lang="scss">
@import "../../styles/variables";
@import "../../styles/classes";
@import "@/styles/themes/chads.scss";

form {
  margin: 30px;
  padding: 30px;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
  min-width: 250px;

  .card-line {
    display: flex;
    justify-content: space-between;
    @media (max-width: 600px) {
      flex-direction: column;
    }

    .card-expiry {
      margin-right: 1em;
      max-width: 50%;
      @media (max-width: 600px) {
        width: 100%;
        margin-right: 0;
      }

      .input-group {
        display: flex;
        align-items: flex-end;

        input.month {
          margin-right: 0.5em;
          width: 50px;
        }

        input.year {
          width: 50px;
        }
      }
    }

    .card_key {
      text-align: right;
      max-width: 50%;

      @media (max-width: 600px) {
        width: 100%;
        max-width: 100%;
      }

      input {
        display: inline-block;
        width: 100px;

        @media (max-width: 600px) {
          width: 100%;
        }
      }
    }
  }

  .legals {
    a {
      font-weight: bold;
      text-decoration: underline;
    }

    span {
      font-size: 0.75em;
    }
  }
}

.buttons-container {
  text-align: center;

  .submit {
    @extend %fresh-btn;
    font-size: 2em;
  }
}
</style>
